/**
 * Browser entry. Builds the FrontendContext from the shared packages and mounts
 * the app into #app. This is the only entry — there is no switching shell.
 */
import type { Locale } from "@mb-group/shared-types";
import { DEFAULT_LOCALE } from "@mb-group/shared-types";
import { createI18n, isLocale } from "@mb-group/i18n";
import { createClient } from "@mb-group/graphql-client";
import { createAnalytics } from "@mb-group/analytics";
import { createFrontend } from "./main.js";
import { createSessionAuth } from "./core/session-auth.js";

/**
 * Read a build-time env var as a string.
 *
 * Vite's own `ImportMetaEnv` carries an `[key: string]: any` index signature, so
 * reading it directly leaks `any` into everything downstream. This narrows once,
 * here, and returns undefined for anything that is not a non-empty string.
 */
function envString(key: string): string | undefined {
	const value: unknown = (import.meta.env as Record<string, unknown>)[key];
	return typeof value === "string" && value !== "" ? value : undefined;
}

function detectInitialLocale(): Locale {
	// The path already encodes the locale (/uslugi vs /services); the router will
	// correct us after matching. This is only the pre-match default.
	try {
		const stored = localStorage.getItem("mb-locale");
		if (isLocale(stored)) return stored;
	} catch {
		/* localStorage unavailable */
	}
	const nav = typeof navigator !== "undefined" ? navigator.language.slice(0, 2) : "";
	return isLocale(nav) ? nav : DEFAULT_LOCALE;
}

async function main(): Promise<void> {
	const mountEl = document.getElementById("app");
	if (!mountEl) {
		console.error("frontend: #app mount point not found");
		return;
	}

	const site = envString("VITE_SITE_KEY") ?? "mb-group";
	const i18n = createI18n(detectInitialLocale());

	// Editor session: an HttpOnly cookie set by the gateway (/auth/*). Browsing
	// works without it, so a failure here must not stop the mount.
	const auth = createSessionAuth();
	await auth.refresh().catch(() => undefined);

	// Mutable request context so the client always sees the current locale.
	// The session cookie rides along on same-origin requests; no token is held here.
	const requestContext: { locale: Locale; site: string } = {
		locale: i18n.locale,
		site,
	};
	const client = createClient({ ctx: requestContext });
	const analyticsEndpoint = envString("VITE_ANALYTICS_ENDPOINT");
	const analytics = createAnalytics(
		analyticsEndpoint ? { endpoint: analyticsEndpoint } : undefined,
	);

	i18n.onChange((locale) => {
		requestContext.locale = locale;
		try {
			localStorage.setItem("mb-locale", locale);
		} catch {
			/* ignore */
		}
	});

	const cmsUrl = envString("VITE_CMS_ADMIN_URL");
	const panelUrl = envString("VITE_PANEL_URL");
	const app = createFrontend({
		client,
		i18n,
		auth,
		analytics,
		site,
		...(cmsUrl ? { cmsUrl } : {}),
		...(panelUrl ? { panelUrl } : {}),
	});
	await app.mount(mountEl);
}

void main();
